// @generated-imports
import { applyFiltersAndRender, escapeHTML, recomposeGames, showToast, syncFilterControls, updateContinueButton } from './app.js';
import { buildBackupFile, renderFolderBackupStatus, reportBackupResult } from './backupplus.js';
import { renderListFilterOptions } from './collections.js';
import { CONFIG, dom, state } from './core.js';
import { describeEmuDb, formatIdbKey, getEmuInventory, invalidateEmuInventory, renderConfigList, renderEmuDataList } from './datapanel.js';
import { applyUiScale } from './display.js';
import { formatBytes } from './format.js';
import { layerClosed, layerOpened, openConfirmLayer } from './layers.js';
import { renderLocalRomsList } from './library.js';
import { initLiteMode } from './lite.js';
import { renderOfflineSection } from './offline.js';
import { applySettingSideEffects, getSetting, idbAllKeys, lastBackupAt, loadPlayTime, loadSettings, refreshSaveIndex, updateHeroCta } from './platform.js';
import { loadFavorites, loadPlayCounts, loadRecentGames, renderRecentGames, restoreFilters } from './prefs.js';
import { renderProfileChip } from './profiles.js';
import { renderResumeRow } from './resume.js';
import { refreshStateMeta, shareOrDownloadFile } from './states.js';
// @generated-imports
/* ===========================================================================
   SAVES & BACKUP
   ---------------------------------------------------------------------------
   O EmulatorJS grava save states e afins em bancos IndexedDB e os
   controles/ajustes em chaves ejs-* do localStorage; o EmuTab guarda
   favoritos, recentes, contagens e filtros em chaves emutab:*. Tudo isso
   é invisível e preso a ESTE navegador. O painel inventaria, exporta um
   .json portátil (binários em base64) e importa de volta — backup e
   migração de máquina.
   ======================================================================== */
const BACKUP_LS_PATTERN = /^(emutab:|ejs-|nes-arcade:)/;
const BACKUP_DB_PATTERN = /emulatorjs|^ejs/i;
// v2: valores tipados (Uint8Array etc.) agora guardam o TIPO da view e o
// recorte byteOffset/byteLength — restaurar devolve o mesmo tipo gravado.
// Arquivos v1 continuam restauráveis (compatibilidade mantida na leitura).
const BACKUP_VERSION = 2;
// Nomes que builds do EmulatorJS costumam criar — usados como fallback de
// sondagem quando o navegador não implementa indexedDB.databases()
// (Firefox antigo, alguns WebViews de TV).
const KNOWN_EJS_DB_NAMES = [
  'EmulatorJS-states',
  'EmulatorJS-roms',
  'EmulatorJS-bios',
  'EmulatorJS-core',
  'EmulatorJS-cores',
  'EmulatorJS-Cache', // confirmado no build em produção (cores/ROMs cacheados)
];

// Bancos que ficam FORA do arquivo de backup: o EmulatorJS-Cache guarda
// cores e ROMs re-baixáveis — embutir isso em base64 explodia o tamanho do
// .json exportado sem proteger nada que não se recupere sozinho.
const BACKUP_EXPORT_EXCLUDE = /cache/i;

function bufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function base64ToBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

// Detecção realm-safe (instanceof falha entre contextos/iframes)
function isArrayBufferLike(value) {
  return Object.prototype.toString.call(value) === '[object ArrayBuffer]';
}

async function serializeIdbValue(value) {
  if (isArrayBufferLike(value)) return { kind: 'buffer', data: bufferToBase64(value) };
  if (ArrayBuffer.isView(value)) {
    // Preserva o TIPO (Uint8Array, Int16Array…) e respeita o recorte da
    // view: serializar value.buffer inteiro incluía bytes fora da janela
    // quando byteOffset > 0, e restaurar como ArrayBuffer cru quebrava
    // builds do EmulatorJS que esperam typed array.
    return {
      kind: 'view',
      viewType: value.constructor.name,
      data: bufferToBase64(value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength)),
    };
  }
  if (value && typeof value.arrayBuffer === 'function' && typeof value.size === 'number') {
    // Blob/File de qualquer realm
    return { kind: 'buffer', data: bufferToBase64(await value.arrayBuffer()) };
  }
  // Objetos simples (o EmulatorJS às vezes embrulha o state num objeto)
  return { kind: 'json', data: JSON.parse(JSON.stringify(value, (k, v) => {
    if (isArrayBufferLike(v)) return { __emutabBuffer: bufferToBase64(v) };
    if (ArrayBuffer.isView(v)) {
      return { __emutabView: {
        type: v.constructor.name,
        data: bufferToBase64(v.buffer.slice(v.byteOffset, v.byteOffset + v.byteLength)),
      } };
    }
    return v;
  })) };
}

/** Reconstrói uma typed array a partir do nome do construtor (com fallback
 *  seguro para ArrayBuffer caso o tipo não exista no navegador). */
function restoreTypedView(type, base64) {
  const buffer = base64ToBuffer(base64);
  const Ctor = typeof window[type] === 'function' ? window[type] : null;
  try {
    return Ctor ? new Ctor(buffer) : buffer;
  } catch (_) {
    return buffer;
  }
}

function deserializeIdbValue(entry) {
  if (entry.kind === 'buffer') return base64ToBuffer(entry.data);
  if (entry.kind === 'view') return restoreTypedView(entry.viewType, entry.data);
  return JSON.parse(JSON.stringify(entry.data), (k, v) => {
    if (v && typeof v === 'object' && typeof v.__emutabBuffer === 'string') {
      return base64ToBuffer(v.__emutabBuffer); // formato v1
    }
    if (v && typeof v === 'object' && v.__emutabView && typeof v.__emutabView.data === 'string') {
      return restoreTypedView(v.__emutabView.type, v.__emutabView.data);
    }
    return v;
  });
}

function idbOpen(name, version, storesMeta) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const request = version ? indexedDB.open(name, version) : indexedDB.open(name);
    if (version) {
      // Upgrade de versão com outra conexão aberta (jogo rodando, outra aba)
      // fica BLOQUEADO — sem este handler o await congelava para sempre e a
      // importação parecia "não fazer nada".
      request.onblocked = () => {
        window.setTimeout(() => {
          if (!settled) {
            settled = true;
            reject(new Error('blocked'));
          }
        }, 1500);
      };
    }
    request.onupgradeneeded = () => {
      // Só na importação (com storesMeta): recria as stores do backup.
      const db = request.result;
      (storesMeta || []).forEach((meta) => {
        if (!db.objectStoreNames.contains(meta.name)) {
          db.createObjectStore(meta.name, {
            keyPath: meta.keyPath || undefined,
            autoIncrement: Boolean(meta.autoIncrement),
          });
        }
      });
    };
    request.onsuccess = () => {
      if (settled) { try { request.result.close(); } catch (_) {} return; }
      settled = true;
      resolve(request.result);
    };
    request.onerror = () => {
      if (settled) return;
      settled = true;
      reject(request.error);
    };
  });
}

/** Conta entradas sem carregar valores (o EmulatorJS-Cache tem megabytes —
 *  o resumo do painel não precisa ler tudo só para exibir um número). */
function idbCountStore(db, storeName) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const request = tx.objectStore(storeName).count();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function idbCountAll(dbName) {
  const db = await idbOpen(dbName);
  let total = 0;
  for (const storeName of [...db.objectStoreNames]) {
    // eslint-disable-next-line no-await-in-loop
    total += await idbCountStore(db, storeName);
  }
  db.close();
  return total;
}

function idbReadStore(db, storeName) {
  return new Promise((resolve, reject) => {
    const entries = [];
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const cursor = store.openCursor();
    cursor.onsuccess = () => {
      const c = cursor.result;
      if (!c) { resolve({ entries, keyPath: store.keyPath, autoIncrement: store.autoIncrement }); return; }
      entries.push({ key: c.key, value: c.value });
      c.continue();
    };
    cursor.onerror = () => reject(cursor.error);
  });
}

/** Verifica se um banco existe SEM criá-lo: se onupgradeneeded disparar
 *  (oldVersion 0 = não existia), aborta a transação de upgrade — o banco
 *  vazio não chega a ser gravado no disco. */
function idbDatabaseExists(name) {
  return new Promise((resolve) => {
    let request;
    try {
      request = indexedDB.open(name);
    } catch (_) {
      resolve(false);
      return;
    }
    request.onupgradeneeded = () => {
      try { request.transaction.abort(); } catch (_) {}
    };
    request.onsuccess = () => {
      try { request.result.close(); } catch (_) {}
      resolve(true);
    };
    request.onerror = () => resolve(false);
    request.onblocked = () => resolve(false);
  });
}

async function listEmulatorDatabases() {
  if (!window.indexedDB) return [];
  if (typeof indexedDB.databases === 'function') {
    try {
      const all = await indexedDB.databases();
      return (all || []).filter((db) => db && db.name && (BACKUP_DB_PATTERN.test(db.name) || EMUTAB_BACKUP_DBS.includes(db.name)));
    } catch (_) {
      // cai para a sondagem por nomes conhecidos
    }
  }
  // Fallback (navegadores sem databases()): antes retornávamos [] e o painel
  // dizia "nenhum save encontrado" mesmo com saves presentes.
  const found = [];
  for (const name of KNOWN_EJS_DB_NAMES.concat(EMUTAB_BACKUP_DBS)) {
    // eslint-disable-next-line no-await-in-loop
    if (await idbDatabaseExists(name)) found.push({ name });
  }
  return found;
}

/** Monta o objeto de backup completo (localStorage + IndexedDB). */
// Bancos do próprio EmuTab que entram no backup (além dos do emulador).
const EMUTAB_BACKUP_DBS = ['emutab-states', 'emutab-library'];

/** Um banco pode entrar no backup? Emulador (sem cache) ou do EmuTab. */
function isBackupDbAllowed(name) {
  if (!name || BACKUP_EXPORT_EXCLUDE.test(name)) return false;
  return BACKUP_DB_PATTERN.test(name) || EMUTAB_BACKUP_DBS.includes(name);
}

async function createBackupObject() {
  const backup = {
    format: 'emutab-backup',
    version: BACKUP_VERSION,
    createdAt: new Date().toISOString(),
    appVersion: CONFIG.appVersion,
    emulatorBuild: CONFIG.emulatorBuild,
    partial: false,
    inventory: { expected: [], exported: [], failed: [] },
    localStorage: {},
    databases: [],
  };

  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && BACKUP_LS_PATTERN.test(key)) {
      backup.localStorage[key] = localStorage.getItem(key);
    }
  }

  const databases = await listEmulatorDatabases();
  for (const name of EMUTAB_BACKUP_DBS) {
    if (!databases.some((meta) => meta.name === name) && await idbDatabaseExists(name)) {
      databases.push({ name });
    }
  }
  for (const meta of databases) {
    if (!isBackupDbAllowed(meta.name)) continue; // cache é re-baixável
    backup.inventory.expected.push(meta.name);
    try {
      const db = await idbOpen(meta.name);
      const dbDump = { name: db.name, version: db.version, stores: [] };
      for (const storeName of [...db.objectStoreNames]) {
        const { entries, keyPath, autoIncrement } = await idbReadStore(db, storeName);
        const serialized = [];
        for (const entry of entries) {
          serialized.push({ key: entry.key, value: await serializeIdbValue(entry.value) });
        }
        dbDump.stores.push({ name: storeName, keyPath, autoIncrement, entries: serialized });
      }
      db.close();
      backup.databases.push(dbDump);
      backup.inventory.exported.push(meta.name);
    } catch (_) {
      // Antes, um banco inacessível era simplesmente omitido e o arquivo
      // saía como "sucesso". Agora o backup é marcado como PARCIAL.
      backup.inventory.failed.push(meta.name);
      backup.partial = true;
    }
  }

  return backup;
}

/** Valida o arquivo INTEIRO antes de qualquer escrita. */
function validateBackupObject(backup) {
  if (!backup || typeof backup !== 'object') throw new Error('Arquivo não é um backup do EmuTab.');
  if (backup.format !== 'emutab-backup') throw new Error('Arquivo não é um backup do EmuTab.');
  if (typeof backup.localStorage !== 'object' || backup.localStorage === null) throw new Error('Backup sem seção de configurações.');
  if (backup.databases !== undefined && !Array.isArray(backup.databases)) throw new Error('Seção de bancos inválida.');
  for (const db of backup.databases || []) {
    if (!db || typeof db.name !== 'string' || !Array.isArray(db.stores)) throw new Error(`Banco inválido no backup: ${db && db.name}`);
    for (const store of db.stores) {
      if (!store || typeof store.name !== 'string' || !Array.isArray(store.entries)) throw new Error(`Store inválida em ${db.name}`);
      for (const entry of store.entries) {
        if (!entry || !entry.value || typeof entry.value.kind !== 'string') throw new Error(`Item inválido em ${db.name}/${store.name}`);
      }
    }
  }
  return true;
}

/** Tipo lógico de uma chave, ignorando o perfil: "emutab:p:ana:favorites"
 *  e "emutab:favorites" são ambos "favorites". */
function backupKeyKind(key) {
  const profiled = /^emutab:p:[^:]+:(.+)$/.exec(key);
  if (profiled) return profiled[1];
  if (key === 'nes-arcade:last-game-id') return 'last-game-id';
  const plain = /^emutab:(.+)$/.exec(key);
  return plain ? plain[1] : key;
}

/** Mescla o valor de uma chave do localStorage (modo mesclar). Regras:
 *  favoritos, "jogar depois" → união; recentes → existentes + novos, com
 *  limite; contagens e tempo → soma; último jogado → mais recente;
 *  coleções → une por id; conquistas → data mais antiga; contadores →
 *  maior valor; perfis → une por id; demais → o que já existe prevalece. */
function mergeLocalStorageValue(key, currentRaw, incomingRaw) {
  const parse = (raw) => { try { return JSON.parse(raw); } catch (_) { return undefined; } };
  const current = parse(currentRaw);
  const incoming = parse(incomingRaw);
  const kind = backupKeyKind(key);
  const bothArrays = Array.isArray(current) && Array.isArray(incoming);
  const bothObjects = current && incoming && typeof current === 'object' && typeof incoming === 'object' && !Array.isArray(current) && !Array.isArray(incoming);
  if ((kind === 'favorites' || kind === 'play-later') && bothArrays) {
    return JSON.stringify([...new Set([...current, ...incoming].map(String))]);
  }
  if (kind === 'recent-games' && bothArrays) {
    return JSON.stringify([...new Set([...current, ...incoming])].slice(0, CONFIG.recentGamesLimit));
  }
  if ((kind === 'play-counts' || kind === 'play-time') && bothObjects) {
    const merged = { ...current };
    Object.keys(incoming).forEach((id) => { merged[id] = (Number(merged[id]) || 0) + (Number(incoming[id]) || 0); });
    return JSON.stringify(merged);
  }
  if ((kind === 'last-played' || kind === 'counters') && bothObjects) {
    const merged = { ...current };
    Object.keys(incoming).forEach((id) => { merged[id] = Math.max(Number(merged[id]) || 0, Number(incoming[id]) || 0); });
    return JSON.stringify(merged);
  }
  if (kind === 'achievements' && bothObjects) {
    const merged = { ...current };
    Object.keys(incoming).forEach((id) => { merged[id] = merged[id] ? Math.min(Number(merged[id]), Number(incoming[id]) || Number(merged[id])) : incoming[id]; });
    return JSON.stringify(merged);
  }
  if ((kind === 'collections' || kind === 'profiles') && bothArrays) {
    const byId = new Map(current.filter((c) => c && c.id).map((c) => [c.id, { ...c }]));
    incoming.filter((c) => c && c.id).forEach((c) => {
      const existing = byId.get(c.id);
      if (!existing) byId.set(c.id, { ...c });
      else if (kind === 'collections' && Array.isArray(existing.games) && Array.isArray(c.games)) existing.games = [...new Set([...existing.games, ...c.games])];
    });
    return JSON.stringify([...byId.values()]);
  }
  return currentRaw; // configurações, filtros, controles: o local prevalece
}

async function restoreBackupObject(backup, options = {}) {
  const merge = options.mode === 'merge';
  validateBackupObject(backup);
  const report = { restoredEntries: 0, failedDbs: 0, skippedDbs: 0, storageErrors: 0 };

  Object.keys(backup.localStorage).forEach((key) => {
    if (!BACKUP_LS_PATTERN.test(key)) return;
    try {
      const current = localStorage.getItem(key);
      if (merge && current !== null) {
        localStorage.setItem(key, mergeLocalStorageValue(key, current, backup.localStorage[key]));
      } else {
        localStorage.setItem(key, backup.localStorage[key]);
      }
    } catch (_) {
      report.storageErrors += 1; // antes era engolido e o toast dizia "sem falhas"
    }
  });

  for (const dbDump of backup.databases || []) {
    // Só bancos do emulador e do EmuTab; cache e nomes arbitrários da mesma
    // origem (outros apps em www.naves.com.br) ficam de fora.
    if (!isBackupDbAllowed(dbDump.name)) { report.skippedDbs += 1; continue; }
    try {
      const db = await openForRestore(dbDump);
      for (const storeDump of dbDump.stores) {
        if (!db.objectStoreNames.contains(storeDump.name)) continue;
        // No modo mesclar, as chaves já presentes ficam intocadas: o backup
        // só COMPLETA o que falta (saves de outra máquina, por exemplo).
        let existingKeys = new Set();
        if (merge) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const keys = await idbAllKeys(db, storeDump.name);
            existingKeys = new Set(keys.map((key) => formatIdbKey(key)));
          } catch (_) { existingKeys = new Set(); }
        }
        // Só conta o que a TRANSAÇÃO confirmou (antes contava no put()).
        const written = await new Promise((resolve, reject) => {
          const tx = db.transaction(storeDump.name, 'readwrite');
          const store = tx.objectStore(storeDump.name);
          let count = 0;
          storeDump.entries.forEach((entry) => {
            if (merge && existingKeys.has(formatIdbKey(entry.key))) return;
            const value = deserializeIdbValue(entry.value);
            // keyPath embutido → put(value); senão → put(value, key)
            if (store.keyPath) store.put(value);
            else store.put(value, entry.key);
            count += 1;
          });
          tx.oncomplete = () => resolve(count);
          tx.onerror = () => reject(tx.error);
          tx.onabort = () => reject(tx.error || new Error('abort'));
        });
        report.restoredEntries += written;
      }
      db.close();
    } catch (_) {
      report.failedDbs += 1; // bloqueado (jogo aberto) ou inacessível — reportado no toast
    }
  }

  return report;
}

/** Abre o banco para restauração. Se o backup vier de uma versão MENOR que
 *  a atual do navegador, o open versionado lança VersionError — antes isso
 *  era engolido e o banco pulado em silêncio; agora reabrimos sem versão
 *  (as stores já existem) e restauramos normalmente. */
async function openForRestore(dbDump) {
  try {
    return await idbOpen(dbDump.name, dbDump.version, dbDump.stores);
  } catch (error) {
    if (error && error.name === 'VersionError') return idbOpen(dbDump.name);
    throw error;
  }
}

async function renderBackupSummary() {
  const lsCount = Object.keys(await (async () => {
    const found = {};
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && BACKUP_LS_PATTERN.test(key)) found[key] = true;
    }
    return found;
  })()).length;

  const parts = [`<div class="backup-row"><strong>${lsCount}</strong> configurações (favoritos, controles, filtros…)</div>`];

  if (window.indexedDB && typeof indexedDB.databases !== 'function') {
    parts.push('<div class="backup-row">⚠️ Este navegador não enumera bancos IndexedDB — a busca usa nomes conhecidos do EmulatorJS e pode não cobrir builds customizados.</div>');
  }

  // Inventário compartilhado com a seção de gestão: cada banco é aberto e
  // contado UMA vez por abertura do painel (antes, duas).
  const inventory = await getEmuInventory();
  if (!inventory.length) {
    parts.push('<div class="backup-row">Nenhum banco de save states encontrado ainda — eles aparecem depois do primeiro save no jogo.</div>');
  }
  for (const item of inventory) {
    const info = describeEmuDb(item.name);
    if (item.total === null) {
      parts.push(`<div class="backup-row">${info.icon} ${escapeHTML(info.label)} <code>${escapeHTML(item.name)}</code> (inacessível)</div>`);
      continue;
    }
    const cacheTag = BACKUP_EXPORT_EXCLUDE.test(item.name)
      ? ' <small>(fica fora do arquivo de backup)</small>'
      : '';
    parts.push(`<div class="backup-row"><strong>${item.total}</strong> ite${item.total === 1 ? 'm' : 'ns'} em ${info.icon} ${escapeHTML(info.label)} <code>${escapeHTML(item.name)}</code>${cacheTag}</div>`);
  }

  const last = lastBackupAt();
  parts.push(`<div class="backup-row">Último backup exportado: <strong>${last ? new Date(last).toLocaleDateString('pt-BR') : 'nunca'}</strong>${last ? ` (há ${Math.max(0, Math.floor((Date.now() - last) / 86400000))} dias)` : ''}.</div>`);

  // Uso total do site (IndexedDB + caches): contexto para decidir a hora
  // de exportar/limpar — e sinal precoce de pressão de quota em TV.
  if (navigator.storage && typeof navigator.storage.estimate === 'function') {
    try {
      const { usage, quota } = await navigator.storage.estimate();
      if (typeof usage === 'number' && typeof quota === 'number' && quota > 0) {
        // Acima de 80% o navegador (TV/Android em especial) começa a apagar
        // dados por pressão de espaço — inclusive save states.
        const nearFull = usage / quota > 0.8;
        parts.push(`<div class="backup-row${nearFull ? ' is-warning' : ''}">Armazenamento do site: <strong>${formatBytes(usage)}</strong> usados de ${formatBytes(quota)} disponíveis.${nearFull ? ' ⚠️ Espaço quase cheio — considere apagar o cache de núcleos e ROMs (re-baixável) e exportar um backup dos saves.' : ''}</div>`);
      }
    } catch (_) { /* estimativa é opcional */ }
  }

  dom.backupSummary.innerHTML = parts.join('');
}

function openBackupModal() {
  // Guarda quem abriu para devolver o foco no fechamento (o game modal e o
  // seletor de core já faziam isso; o backup deixava o foco órfão no body).
  state.backupLastFocus = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null;
  dom.backupModal.classList.remove('is-hidden');
  layerOpened('backup', dom.backupClose);
  renderOfflineSection();
  renderFolderBackupStatus();
  state.emuExpandedDbs = new Set(); // painel abre sempre recolhido
  state.emuDataEntries = new Map(); // registro de itens começa limpo
  invalidateEmuInventory();         // recontagem fresca, compartilhada
  renderBackupSummary();
  renderEmuDataList();
  renderConfigList();
  renderLocalRomsList();
}

function closeBackupModal() {
  dom.backupModal.classList.add('is-hidden');
  layerClosed('backup');
}

async function exportBackup() {
  dom.backupExportBtn.disabled = true;
  try {
    // Nome com data e contagem (backupplus.js); backup parcial não conta
    // como "último backup completo" e o lembrete continua.
    const { file, backup } = await buildBackupFile();
    await shareOrDownloadFile(file, 'Backup do EmuTab', { downloadOnly: true });
    reportBackupResult(backup, 'exportado');
  } catch (error) {
    showToast('Não foi possível exportar o backup.');
  } finally {
    dom.backupExportBtn.disabled = false;
  }
}

/** Lê o arquivo, mostra uma PRÉVIA e pede confirmação antes de tocar nos
 *  dados — antes, um clique errado sobrescrevia saves sem aviso. O usuário
 *  escolhe entre substituir e mesclar (só o que ainda não existe). */
async function startImportFlow(file) {
  if (state.currentIframe) {
    showToast('Desligue o jogo antes de importar — com o jogo aberto o emulador trava os bancos.');
    return;
  }
  if (!getSetting('confirmImport')) {
    importBackup(file, { mode: 'replace' });
    return;
  }
  let backup = null;
  try {
    backup = JSON.parse(await file.text());
  } catch (_) {
    showToast('Arquivo de backup inválido.');
    return;
  }
  if (!backup || backup.format !== 'emutab-backup') {
    showToast('Este arquivo não é um backup do EmuTab.');
    return;
  }

  const saveCount = (backup.databases || [])
    .filter((db) => !BACKUP_EXPORT_EXCLUDE.test(db.name))
    .reduce((sum, db) => sum + (db.stores || []).reduce((acc, store) => acc + (store.entries || []).length, 0), 0);
  const configCount = Object.keys(backup.localStorage || {}).length;
  const when = backup.createdAt ? new Date(backup.createdAt).toLocaleString('pt-BR') : 'data desconhecida';
  const summary = `Backup de ${when} — ${saveCount} item(ns) de save e ${configCount} configuração(ões).`;

  openConfirmLayer({
    title: 'Importar backup',
    message: `${summary} "Substituir" sobrescreve o que existe com o mesmo nome; "Mesclar" une favoritos, recentes, contagens e só adiciona saves que ainda não existem aqui.`,
    actions: [
      { label: 'Substituir', kind: 'danger', handler: () => importBackup(file, { mode: 'replace' }) },
      { label: 'Mesclar', handler: () => importBackup(file, { mode: 'merge' }) },
    ],
  });
}

async function importBackup(file, options = {}) {
  if (state.currentIframe) {
    showToast('Desligue o jogo antes de importar — com o jogo aberto o emulador trava os bancos e a restauração falha.');
    return;
  }
  try {
    const text = await file.text();
    const backup = JSON.parse(text);
    const result = await restoreBackupObject(backup, options);
    // Recarrega TUDO o que vive em memória a partir do que acabou de entrar
    // (settings e tempo de jogo ficavam de fora e podiam regravar dados
    // antigos na próxima alteração).
    loadFavorites();
    loadRecentGames();
    loadPlayCounts();
    loadPlayTime();
    loadSettings();
    applySettingSideEffects('wakeLock');
    applySettingSideEffects('padLegend');
    applySettingSideEffects('showcase');
    initLiteMode();
    applyUiScale();
    await refreshStateMeta();
    renderProfileChip();
    renderListFilterOptions();
    renderResumeRow();
    recomposeGames();
    restoreFilters();
    syncFilterControls();
    applyFiltersAndRender();
    renderRecentGames();
    updateContinueButton();
    invalidateEmuInventory();
    refreshSaveIndex().then(() => applyFiltersAndRender());
    updateHeroCta();
    renderBackupSummary();
    if (!dom.backupModal.classList.contains('is-hidden')) {
      // O botão Importar mora no painel: as seções de gestão precisam
      // refletir o que acabou de entrar, não o estado pré-import.
      renderEmuDataList();
      renderConfigList();
      renderLocalRomsList();
    }
    const notes = [];
    if (result.failedDbs) notes.push(`${result.failedDbs} banco(s) não puderam ser restaurados — feche outras abas do EmuTab e tente de novo`);
    if (result.storageErrors) notes.push(`${result.storageErrors} configuração(ões) não puderam ser gravadas (armazenamento cheio?)`);
    if (result.skippedDbs) notes.push(`${result.skippedDbs} banco(s) fora do escopo do EmuTab ignorados`);
    const prefix = (result.failedDbs || result.storageErrors) ? 'Backup importado com falhas' : 'Backup importado';
    showToast(`${prefix} (${result.restoredEntries} itens de save restaurados).${notes.length ? ' ' + notes.join('; ') + '.' : ''}`);
  } catch (error) {
    showToast('Arquivo de backup inválido ou incompatível.');
  }
}

// @generated-exports
export { BACKUP_DB_PATTERN, BACKUP_EXPORT_EXCLUDE, BACKUP_LS_PATTERN, BACKUP_VERSION, EMUTAB_BACKUP_DBS, KNOWN_EJS_DB_NAMES, backupKeyKind, base64ToBuffer, bufferToBase64, closeBackupModal, createBackupObject, deserializeIdbValue, exportBackup, idbCountAll, idbCountStore, idbDatabaseExists, idbOpen, idbReadStore, importBackup, isArrayBufferLike, isBackupDbAllowed, listEmulatorDatabases, mergeLocalStorageValue, openBackupModal, openForRestore, renderBackupSummary, restoreBackupObject, restoreTypedView, serializeIdbValue, startImportFlow, validateBackupObject };
// @generated-exports
